/**
 * documentCache.js
 *
 * Isolated localStorage-backed LRU cache for document data + info.
 * Stored under a single key as a map: { [documentId]: entry }.
 *
 * entry shape:
 *   {
 *     documentId,
 *     documentData,   // merged grid payload (includes spreadsheetData)
 *     documentInfo,   // result of getDocumentById (permissions/meta)
 *     lastAccessedAt, // ISO string - drives LRU eviction
 *     updatedAt,      // ISO string - last time fresh data was written
 *   }
 *
 * This module is intentionally framework-agnostic and never throws into render:
 * every localStorage interaction is wrapped in try/catch and degrades to a no-op.
 */

export const CACHE_KEY = "cache_documents_grid";
export const MAX_ENTRIES = 5;

function readCacheMap() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("documentCache: failed to read cache", error);
    return {};
  }
}

function writeCacheMap(map) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(map));
  } catch (error) {
    console.error("documentCache: failed to write cache", error);
  }
}

/**
 * Evict least-recently-used entries until the map holds at most MAX_ENTRIES.
 * "Least recently used" = lowest lastAccessedAt.
 */
function enforceLruLimit(map) {
  const ids = Object.keys(map);
  if (ids.length <= MAX_ENTRIES) return map;

  const sortedByAccess = ids.sort((a, b) => {
    const aTime = new Date(map[a]?.lastAccessedAt || 0).getTime();
    const bTime = new Date(map[b]?.lastAccessedAt || 0).getTime();
    return aTime - bTime; // oldest first
  });

  const evictCount = ids.length - MAX_ENTRIES;
  for (let i = 0; i < evictCount; i += 1) {
    delete map[sortedByAccess[i]];
  }
  return map;
}

/**
 * Cheap, non-mutating check for a *renderable* cached entry (has sheet data).
 * Does NOT bump lastAccessedAt - safe to call during render.
 */
export function hasCachedDocument(documentId) {
  if (!documentId) return false;
  const entry = readCacheMap()[documentId];
  return Array.isArray(entry?.documentData?.spreadsheetData)
    && entry.documentData.spreadsheetData.length > 0;
}

/**
 * Read a cached entry. On a hit, bump lastAccessedAt (LRU touch) and persist.
 * Returns the entry or null.
 */
export function getCachedDocument(documentId) {
  if (!documentId) return null;
  const map = readCacheMap();
  const entry = map[documentId];
  if (!entry) return null;

  entry.lastAccessedAt = new Date().toISOString();
  map[documentId] = entry;
  writeCacheMap(map);
  return entry;
}

/**
 * Upsert a cache entry with the latest merged data, refresh timestamps,
 * then enforce the LRU limit. Returns the persisted entry (or null on no-op).
 */
export function setCachedDocument(documentId, { documentData, documentInfo } = {}) {
  if (!documentId) return null;
  const now = new Date().toISOString();
  const map = readCacheMap();
  const existing = map[documentId];

  const entry = {
    documentId,
    documentData: documentData !== undefined ? documentData : existing?.documentData ?? null,
    documentInfo: documentInfo !== undefined ? documentInfo : existing?.documentInfo ?? null,
    lastAccessedAt: now,
    updatedAt: now,
  };

  map[documentId] = entry;
  enforceLruLimit(map);
  writeCacheMap(map);
  return entry;
}

/**
 * Return the merged cached payload for rendering, or null when absent.
 * Touches lastAccessedAt via getCachedDocument.
 */
export function getMergedCachedData(documentId) {
  const entry = getCachedDocument(documentId);
  if (!entry) return null;
  return {
    documentData: entry.documentData ?? null,
    documentInfo: entry.documentInfo ?? null,
  };
}
